import React, { FC, memo } from 'react';
import { ImageMedia, VideoMedia } from '~/lib/data-contract';
import { MediaImage, Video } from '~/shared/components';
import { getObjectPosition } from '~/shared/utils';
import { useModule } from '~/templates';
import { queries } from '~/theme';
import styles from './hero-media.module.css';
import { useCookieConsent } from '~/shared/hooks';

const getImgSizes = (index: number, isFullWidth: boolean) => {
    if (isFullWidth) {
        return index < 2 ? '80vw' : '100vw';
    }
    return `${queries.md} 67vw, ${index < 2 ? '80vw' : '100vw'}`;
};

type Props = {
    image?: ImageMedia;
    video?: VideoMedia;
    size?: 'full' | 'split';
};

const HeroMedia: FC<Props> = memo((props) => {
    const { image, video, size = 'full' } = props;
    const imgSrc = image?.src || '';
    const videoSrc = video?.src || '';
    const isFullWidth = size === 'full';

    const { index } = useModule();
    const { marketing, statistic } = useCookieConsent();

    if (!imgSrc && !videoSrc) {
        return null;
    }

    const shouldRenderVideo =
        videoSrc && ((marketing && statistic) || (!(marketing && statistic) && !imgSrc));

    const imgSizes = getImgSizes(index, isFullWidth);

    const objectPosition = getObjectPosition(image?.focalPoint);

    if (shouldRenderVideo) {
        return (
            <Video
                controls={false}
                src={videoSrc}
                loop={video?.loop}
                playing={isFullWidth || video?.autoPlay}
                posterSrc={imgSrc}
                cover={true}
                buttonPosition={isFullWidth ? 'corner' : 'center'}
                showCustomButtons={false}
                posterSizes={imgSizes}
            />
        );
    }

    if (imgSrc) {
        return (
            <MediaImage
                {...image}
                src={imgSrc}
                fill
                alt={image?.alt || ''}
                className={styles.image}
                sizes={imgSizes}
                style={{
                    objectPosition,
                }}
            />
        );
    }

    return null;

});

HeroMedia.displayName = 'HeroMedia';

export default HeroMedia;
